import {
  useCallback,
  useEffect,
  useState,
} from 'react';

export type FinancialAccount = {
  id: string;
  name: string;
  accountType: string;
  institution: string | null;
  currency: string;
  openingBalanceCents: number;
  openingBalanceDate: string | null;
  receiptCents: number;
  reversalCents: number;
  ledgerCents: number;
  currentBalanceCents: number;
  movementCount: number;
  isActive: boolean;
  notes: string | null;
};

export type CreateFinancialAccountInput = {
  name: string;
  accountType: string;
  institution?: string | null;
  openingBalanceCents: number;
  openingBalanceDate?: string | null;
  notes?: string | null;
};

export type UpdateFinancialAccountInput =
  Partial<CreateFinancialAccountInput> & {
    isActive?: boolean;
  };

export type SettlementAllocationInput = {
  installmentId: string;
  amountCents: number;
};

export type CreateSettlementInput = {
  paymentId: string;
  amountCents: number;
  occurredAt: string;
  paymentMethod: string;
  financialAccountId: string;
  receiptReference?: string | null;
  notes?: string | null;
  externalReference?: string | null;
  idempotencyKey: string;
  allocations?: SettlementAllocationInput[];
};

export type PaymentSettlement = {
  id: string;
  paymentId: string;
  kind: string;
  amountCents: number;
  occurredAt: string;
  paymentMethod: string | null;
  financialAccountId: string | null;
  financialAccountName: string | null;
  receiptReference: string | null;
  notes: string | null;
  source: string;
  externalReference: string | null;
  idempotencyKey: string | null;
  reversalOfId: string | null;
  createdByUserId: string | null;
  createdByName: string | null;
  createdAt: string;
  allocations: Array<{
    id: string;
    installmentId: string;
    amountCents: number;
    createdAt: string;
  }>;
};

export function useFinanceSettlements() {
  const [
    financialAccounts,
    setFinancialAccounts,
  ] = useState<FinancialAccount[]>([]);

  const [
    allFinancialAccounts,
    setAllFinancialAccounts,
  ] = useState<FinancialAccount[]>([]);

  const [
    isLoadingFinancialAccounts,
    setIsLoadingFinancialAccounts,
  ] = useState(false);

  const [
    isLoadingAllFinancialAccounts,
    setIsLoadingAllFinancialAccounts,
  ] = useState(false);

  const [
    financialAccountsManagementError,
    setFinancialAccountsManagementError,
  ] = useState<string | null>(null);

  const [
    financialAccountsError,
    setFinancialAccountsError,
  ] = useState<string | null>(null);

  const loadFinancialAccounts =
    useCallback(async () => {
      setIsLoadingFinancialAccounts(true);
      setFinancialAccountsError(null);

      try {
        const response =
          await fetch(
            '/api/financial-accounts',
            {
              credentials: 'include',
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !Array.isArray(
            data.accounts
          )
        ) {
          throw new Error(
            data.error ||
              `GET /api/financial-accounts falhou com status ${response.status}`
          );
        }

        setFinancialAccounts(
          data.accounts
        );

        return data.accounts as
          FinancialAccount[];
      } catch (error: any) {
        setFinancialAccounts([]);

        setFinancialAccountsError(
          error?.message ||
            'Não foi possível carregar as contas financeiras.'
        );

        return [];
      } finally {
        setIsLoadingFinancialAccounts(
          false
        );
      }
    }, []);

  const loadAllFinancialAccounts =
    useCallback(async () => {
      setIsLoadingAllFinancialAccounts(true);
      setFinancialAccountsManagementError(null);

      try {
        const response =
          await fetch(
            '/api/financial-accounts?includeInactive=1',
            {
              credentials: 'include',
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !Array.isArray(
            data.accounts
          )
        ) {
          throw new Error(
            data.error ||
              `GET contas financeiras falhou com status ${response.status}`
          );
        }

        setAllFinancialAccounts(
          data.accounts
        );

        return data.accounts as
          FinancialAccount[];
      } catch (error: any) {
        setAllFinancialAccounts([]);

        setFinancialAccountsManagementError(
          error?.message ||
            'Não foi possível carregar as contas financeiras.'
        );

        return [];
      } finally {
        setIsLoadingAllFinancialAccounts(
          false
        );
      }
    }, []);

  const createFinancialAccount =
    useCallback(
      async (
        input:
          CreateFinancialAccountInput
      ) => {
        const response =
          await fetch(
            '/api/financial-accounts',
            {
              method: 'POST',
              credentials: 'include',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body:
                JSON.stringify(
                  input
                ),
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !data.account
        ) {
          throw new Error(
            data.error ||
              `POST conta financeira falhou com status ${response.status}`
          );
        }

        await Promise.all([
          loadFinancialAccounts(),
          loadAllFinancialAccounts(),
        ]);

        return data.account as
          FinancialAccount;
      },
      [
        loadFinancialAccounts,
        loadAllFinancialAccounts,
      ]
    );

  const updateFinancialAccount =
    useCallback(
      async (
        accountId: string,
        input:
          UpdateFinancialAccountInput
      ) => {
        const response =
          await fetch(
            `/api/financial-accounts/${encodeURIComponent(accountId)}`,
            {
              method: 'PATCH',
              credentials: 'include',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body:
                JSON.stringify(
                  input
                ),
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !data.account
        ) {
          throw new Error(
            data.error ||
              `PATCH conta financeira falhou com status ${response.status}`
          );
        }

        await Promise.all([
          loadFinancialAccounts(),
          loadAllFinancialAccounts(),
        ]);

        return data.account;
      },
      [
        loadFinancialAccounts,
        loadAllFinancialAccounts,
      ]
    );

  const createSettlement =
    useCallback(
      async (
        input:
          CreateSettlementInput
      ) => {
        const response =
          await fetch(
            '/api/payment-settlements',
            {
              method: 'POST',
              credentials:
                'include',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body:
                JSON.stringify(
                  input
                ),
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !data.settlement
        ) {
          throw new Error(
            data.error ||
              `POST /api/payment-settlements falhou com status ${response.status}`
          );
        }

        return {
          settlement:
            data.settlement as PaymentSettlement,

          duplicated:
            Boolean(
              data.duplicated
            ),

          payment:
            data.payment ?? null,
        };
      },
      []
    );

  const loadSettlements =
    useCallback(
      async (
        paymentId: string
      ) => {
        const params =
          new URLSearchParams({
            paymentId,
          });

        const response =
          await fetch(
            `/api/payment-settlements?${params.toString()}`,
            {
              credentials:
                'include',
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !Array.isArray(
            data.settlements
          )
        ) {
          throw new Error(
            data.error ||
              `GET /api/payment-settlements falhou com status ${response.status}`
          );
        }

        return (
          data.settlements as
            PaymentSettlement[]
        );
      },
      []
    );

  const reverseSettlement =
    useCallback(
      async (
        settlementId: string,
        input: {
          reason: string;
          occurredAt?: string;
        }
      ) => {
        const response =
          await fetch(
            `/api/payment-settlements/${encodeURIComponent(settlementId)}/reverse`,
            {
              method: 'POST',
              credentials:
                'include',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body:
                JSON.stringify(
                  input
                ),
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !data.reversal
        ) {
          throw new Error(
            data.error ||
              `POST estorno falhou com status ${response.status}`
          );
        }

        return data;
      },
      []
    );

  useEffect(() => {
    void loadFinancialAccounts();
    void loadAllFinancialAccounts();
  }, [
    loadFinancialAccounts,
    loadAllFinancialAccounts,
  ]);

  return {
    financialAccounts,
    allFinancialAccounts,
    isLoadingFinancialAccounts,
    isLoadingAllFinancialAccounts,
    financialAccountsError,
    financialAccountsManagementError,
    loadFinancialAccounts,
    loadAllFinancialAccounts,
    createFinancialAccount,
    updateFinancialAccount,
    createSettlement,
    loadSettlements,
    reverseSettlement,
  };
}